// §12 key transparency — the per-account append-only transparency log.
// JS twin of the C# Translog support in E2EEManager/E2EEModels; the interop
// vector suite keeps the two byte-compatible. Loaded by Node (server, dev-cli,
// verify-vectors) and the browser pages.
//
// One entry per mutation of a signed key document (device list, member list,
// identity). Entries are hash-chained (prevHash) and signed by the account
// identity signing key CURRENT AT THAT SEQ. An identity rotation is a
// cross-signed 'identity' entry: `sig` by the OUTGOING key authorizes the
// transfer, `newSignerSig` by the incoming key proves possession — so trust
// in identity v1 mechanically extends to v2 and the server can no longer
// substitute keys at a rotation (the old TOFU cliff). seq 0 is the genesis
// trust anchor (TOFU exactly once; `lateGenesis` marks a chain backfilled for
// an account that predates transparency).
//
// Entry shape (canonical JCS subset — integers only):
//   core  = { accountId, seq, prevHash, ts, kind, docVersion, docHash,
//             signerPk, newSignerPk?, lateGenesis? }
//   entry = core + { sig, newSignerSig? }
// signed bytes = canonical(core); entry hash = BLAKE2b-32(canonical(entry)).

import { canonicalizeBytes } from './jcs.js';
import { deviceListSignedBytes, memberListSignedBytes, b64, ub64 } from './envelope.js';

export const KIND_IDENTITY = 'identity';
export const KIND_DEVICE_LIST = 'device-list';
export const KIND_MEMBER_LIST = 'member-list';

const HASH_BYTES = 32;

// ---- doc hashes (over the exact bytes the docs' own signatures cover) ------

export function docHashDeviceList(sodium, list) {
  return b64(sodium, sodium.crypto_generichash(HASH_BYTES, deviceListSignedBytes(list)));
}

/** v2: the member-list doc hash covers `epochPk` too (see memberListSignedBytes). */
export function docHashMemberList(sodium, list) {
  return b64(sodium, sodium.crypto_generichash(HASH_BYTES, memberListSignedBytes(list)));
}

/** identity "doc" = the public identity claim. */
export function docHashIdentity(sodium, { idEncPk, idSignPk, identityVersion }) {
  return b64(sodium, sodium.crypto_generichash(HASH_BYTES, canonicalizeBytes({
    idEncPk, idSignPk, identityVersion,
  })));
}

// ---- entry construction -----------------------------------------------------

function coreOf(entry) {
  const { sig, newSignerSig, ...core } = entry;
  return core;
}

export function entrySignedBytes(entry) {
  return canonicalizeBytes(coreOf(entry));
}

export function entryHash(sodium, entry) {
  return b64(sodium, sodium.crypto_generichash(HASH_BYTES, canonicalizeBytes(entry)));
}

/**
 * Sign a core into a full entry. `signSk` is the key current at this seq
 * (for 'identity' entries: the OUTGOING key); `newSignerSk` is required for
 * 'identity' entries (possession proof by the incoming key — at genesis both
 * are the same key).
 */
export function signEntry(sodium, core, signSk, newSignerSk = null) {
  const entry = { ...core };
  entry.sig = b64(sodium, sodium.crypto_sign_detached(entrySignedBytes(entry), signSk));
  if (core.kind === KIND_IDENTITY) {
    if (!newSignerSk) throw new Error('identity entries must be cross-signed (newSignerSk required)');
    entry.newSignerSig = b64(sodium, sodium.crypto_sign_detached(entrySignedBytes(entry), newSignerSk));
  }
  return entry;
}

/**
 * Append the log entries for one mutating operation and POST them. Shared by
 * dev-cli and the browser pages (both hand in their fetch helpers).
 *
 * spec = {
 *   accountId, ts,
 *   identity: { idEncPk, idSignPk, identityVersion },  // POST-op identity
 *   idSignSk,                                           // its signing key (bytes)
 *   rotation: { oldSignerPk, oldSignSk } | undefined,   // set when this op rotated the identity
 *   deviceList / memberList: post-op doc when this op changed it,
 *   lateGenesis: bool (default true)                    // false only at account creation
 * }
 *
 * An account with an empty log gets a genesis triplet anchoring the POST-op
 * state (identity + both lists) instead of individual entries — this is how
 * pre-transparency accounts are backfilled on their first mutation. Note a
 * rotation on a pre-transparency account therefore anchors the NEW identity;
 * there is no old-key chain to cross-sign against (irreducible: transparency
 * can't retroactively protect history it never saw).
 */
export async function commitDocs(sodium, api, accountEmail, spec) {
  const enc = encodeURIComponent(accountEmail);
  const log = await api.get(`/e2ee/log?email=${enc}`).catch(() => []);
  const entries = [];
  let seq = log.length;
  let prevHash = seq ? entryHash(sodium, log[seq - 1]) : '';
  const push = (core, signSk, newSignerSk = null) => {
    const e = signEntry(sodium, { accountId: spec.accountId, seq, prevHash, ts: spec.ts, ...core }, signSk, newSignerSk);
    entries.push(e);
    prevHash = entryHash(sodium, e);
    seq += 1;
  };
  const idDoc = { kind: KIND_IDENTITY, docVersion: spec.identity.identityVersion, docHash: docHashIdentity(sodium, spec.identity) };

  if (seq === 0) {
    const dl = spec.deviceList ?? await api.get(`/e2ee/deviceList?email=${enc}`);
    const ml = spec.memberList ?? await api.get(`/e2ee/memberList?email=${enc}`);
    push({ ...idDoc, signerPk: spec.identity.idSignPk, newSignerPk: spec.identity.idSignPk,
           ...(spec.lateGenesis === false ? {} : { lateGenesis: true }) },
         spec.idSignSk, spec.idSignSk);
    push({ kind: KIND_DEVICE_LIST, docVersion: dl.version, docHash: docHashDeviceList(sodium, dl), signerPk: spec.identity.idSignPk }, spec.idSignSk);
    push({ kind: KIND_MEMBER_LIST, docVersion: ml.version, docHash: docHashMemberList(sodium, ml), signerPk: spec.identity.idSignPk }, spec.idSignSk);
  } else {
    if (spec.rotation) {
      push({ ...idDoc, signerPk: spec.rotation.oldSignerPk, newSignerPk: spec.identity.idSignPk },
           spec.rotation.oldSignSk, spec.idSignSk);
    }
    if (spec.deviceList) {
      push({ kind: KIND_DEVICE_LIST, docVersion: spec.deviceList.version,
             docHash: docHashDeviceList(sodium, spec.deviceList), signerPk: spec.identity.idSignPk }, spec.idSignSk);
    }
    if (spec.memberList) {
      push({ kind: KIND_MEMBER_LIST, docVersion: spec.memberList.version,
             docHash: docHashMemberList(sodium, spec.memberList), signerPk: spec.identity.idSignPk }, spec.idSignSk);
    }
  }
  for (const e of entries) await api.post(`/e2ee/log?email=${enc}`, { value: e });
  return entries;
}

// ---- chain verification -----------------------------------------------------

/**
 * Verify the FULL chain from genesis (entries must start at seq 0). Throws on
 * ANY inconsistency — broken link, bad signature, non-contiguous seq, signer
 * mismatch. A pinned head ({ seq, hash, signerPk }) from a previous session
 * is used as a continuity checkpoint: the chain must still contain that exact
 * entry hash at that seq (a shorter or divergent chain = rollback/fork alarm).
 * Returns { head: {seq, hash, signerPk}, latest: {identity?, deviceList?, memberList?} }.
 * With no pinned head, the genesis signer is the (once-only) trust anchor.
 */
export function verifyChain(sodium, accountId, entries, pinnedHead = null) {
  if (!entries.length) throw new Error('transparency log is empty');
  if (pinnedHead && entries.length <= pinnedHead.seq) {
    throw new Error(`transparency log REGRESSED: we have pinned seq ${pinnedHead.seq}, server returned ${entries.length} entries`);
  }
  let expectedSeq = 0;
  let prevHash = '';
  let signer = null;
  const latest = { identity: null, deviceList: null, memberList: null };

  for (const entry of entries) {
    if (entry.accountId !== accountId) throw new Error(`log entry ${entry.seq}: wrong accountId`);
    if (entry.seq !== expectedSeq) throw new Error(`log entry gap: expected seq ${expectedSeq}, got ${entry.seq}`);
    if (entry.prevHash !== prevHash) throw new Error(`log entry ${entry.seq}: hash chain broken`);

    // Genesis anchors the signer; afterwards the entry must be signed by the
    // key custody has been traced to.
    if (signer === null) {
      if (entry.seq !== 0 || entry.kind !== KIND_IDENTITY) throw new Error('log does not start with a genesis identity entry');
      signer = entry.signerPk;
    }
    if (entry.signerPk !== signer) throw new Error(`log entry ${entry.seq}: signer is not the chain's current identity key`);
    const signedBytes = entrySignedBytes(entry);
    if (!sodium.crypto_sign_verify_detached(ub64(sodium, entry.sig), signedBytes, ub64(sodium, signer))) {
      throw new Error(`log entry ${entry.seq}: signature is NOT authentic`);
    }

    if (entry.kind === KIND_IDENTITY) {
      if (!entry.newSignerPk || !entry.newSignerSig) throw new Error(`log entry ${entry.seq}: identity entry is not cross-signed`);
      if (!sodium.crypto_sign_verify_detached(
          ub64(sodium, entry.newSignerSig), signedBytes, ub64(sodium, entry.newSignerPk))) {
        throw new Error(`log entry ${entry.seq}: incoming identity possession signature is NOT authentic`);
      }
      signer = entry.newSignerPk; // custody transfers
      latest.identity = entry;
    } else if (entry.kind === KIND_DEVICE_LIST) {
      latest.deviceList = entry;
    } else if (entry.kind === KIND_MEMBER_LIST) {
      latest.memberList = entry;
    } else {
      throw new Error(`log entry ${entry.seq}: unknown kind ${entry.kind}`);
    }

    prevHash = entryHash(sodium, entry);
    if (pinnedHead && entry.seq === pinnedHead.seq && prevHash !== pinnedHead.hash) {
      throw new Error(`transparency log FORKED: entry ${entry.seq} no longer matches the head we pinned earlier`);
    }
    expectedSeq += 1;
  }

  return { head: { seq: expectedSeq - 1, hash: prevHash, signerPk: signer }, latest };
}

/**
 * Bind a fetched doc to the chain: the latest entry of its kind must commit
 * exactly this version + content hash. A doc newer than the chain (version
 * ahead) or altered (hash mismatch) or rolled back (version behind) throws.
 */
export function assertDocCommitted(kind, latestEntry, docVersion, docHash) {
  if (!latestEntry) throw new Error(`transparency log has no ${kind} entry but a ${kind} document exists`);
  if (latestEntry.docVersion !== docVersion) {
    throw new Error(`${kind} version ${docVersion} does not match the transparency log ` +
      `(chain commits version ${latestEntry.docVersion}${docVersion < latestEntry.docVersion ? ' — possible ROLLBACK' : ' — uncommitted mutation'})`);
  }
  if (latestEntry.docHash !== docHash) {
    throw new Error(`${kind} content does not match the transparency log commitment (possible substitution)`);
  }
}

// ---- head pinning -----------------------------------------------------------

/**
 * Default head store: browser localStorage when available, else in-memory
 * (verification still runs from genesis every time; rollback detection is
 * then limited to a single session). dev-cli passes a file-backed store.
 */
const memHeads = new Map();
export function defaultHeadStore() {
  const hasLocal = typeof localStorage !== 'undefined';
  return {
    load(accountId) {
      if (hasLocal) {
        const raw = localStorage.getItem(`nc-translog-head:${accountId}`);
        return raw ? JSON.parse(raw) : null;
      }
      return memHeads.get(accountId) ?? null;
    },
    save(accountId, head) {
      if (hasLocal) localStorage.setItem(`nc-translog-head:${accountId}`, JSON.stringify(head));
      else memHeads.set(accountId, head);
    },
  };
}
